import { type ShortUrlResponse } from '@air/api/types';
import { buildClientImgixUrl } from '@air/component-imgix-image';

import {
  type CoverImageRegion,
  getCoverImageFocalPoint,
} from '~/components/v2/components/showcase/getCoverImageFocalPoint';

type ShowcaseCoverImage = NonNullable<NonNullable<ShortUrlResponse['presentationSettings']>['coverImage']>;

export type PublicShowcaseOgCoverImage = Pick<ShowcaseCoverImage, 'region'> & {
  // `url` is resolved on the public read shape; editor drafts can still carry a cover reference
  // without a preview url, in which case we fall through to the collection thumbnail.
  url?: ShowcaseCoverImage['url'];
};

export type GetPublicShowcaseOgImageUrlParams = {
  coverImage: PublicShowcaseOgCoverImage | undefined;
  thumbnail: string | null | undefined;
  origin: string;
};

const toAbsoluteUrl = (url: string, origin: string): string => (url.startsWith('/') ? `${origin}${url}` : url);

const withCoverCrop = (url: string, region: CoverImageRegion): string => {
  // Cover authoring is a pan (Reposition writes object-position into the region). Pass that
  // movement through as Imgix fp-x/fp-y so the Slack/OG crop follows the same place they framed.
  const { x, y } = getCoverImageFocalPoint(region);

  // 1200×628 is 1.91:1, the Open Graph / Slack large-image unfurl crop (same as clip OG).
  return buildClientImgixUrl(url, {
    w: 1200,
    h: 628,
    fit: 'crop',
    crop: 'focalpoint',
    'fp-x': x,
    'fp-y': y,
    auto: 'format',
  });
};

const withThumbnailClip = (url: string): string =>
  buildClientImgixUrl(url, { w: 1200, h: 1200, fit: 'clip', auto: 'format' });

export const getPublicShowcaseOgImageUrl = ({
  coverImage,
  thumbnail,
  origin,
}: GetPublicShowcaseOgImageUrlParams): string | undefined => {
  // Prefer the header/cover over the first-asset thumbnail so empty showcases with a header still
  // unfurl, and so Slack/OG previews match the image authors actually chose.
  if (coverImage?.url) {
    return toAbsoluteUrl(withCoverCrop(coverImage.url, coverImage.region), origin);
  }

  if (thumbnail) {
    return toAbsoluteUrl(withThumbnailClip(thumbnail), origin);
  }

  return undefined;
};
